/**
 * The card packs. See scope.md §9 and the gallery these were designed in.
 *
 * A pack is purely cosmetic and **per device** — it never enters game state, so two
 * players in the same room can happily use different ones.
 */

import type { Suit } from '@garbage/shared';

export type PackId = 'seaside' | 'space' | 'woodland' | 'clarity';

export type Pack = {
  id: PackId;
  name: string;
  blurb: string;
  /** The card face. */
  face: string;
  faceEdge: string;
  /** Four hues, far enough apart to survive red-green colour blindness. */
  suits: Record<Suit, string>;
  joker: { colour: string; tint: string; edge: string };
  back: { background: string; edge: string; mark: string; markColour: string };
};

export const PACKS: Record<PackId, Pack> = {
  seaside: {
    id: 'seaside',
    name: 'Seaside',
    blurb: 'Chunky and high contrast',
    face: '#f7f2e6',
    faceEdge: 'rgba(20,35,42,0.16)',
    // Near-black spades and a leaf-green club: the old slate blue and teal were too alike.
    suits: { spades: '#131a20', hearts: '#d13b57', diamonds: '#e08b1e', clubs: '#2b7406' },
    joker: { colour: '#6d4bc4', tint: '#f1ecfa', edge: 'rgba(109,75,196,0.45)' },
    back: {
      background: 'repeating-linear-gradient(135deg,#12776a 0 7px,#0e5f55 7px 14px)',
      edge: '#0b4a42',
      mark: '≈',
      markColour: '#bfe8e0',
    },
  },
  space: {
    id: 'space',
    name: 'Space Cadets',
    blurb: 'Neon on near-black',
    face: '#161a24',
    faceEdge: 'rgba(120,170,210,0.28)',
    // White spades and a green club; periwinkle and cyan were too alike.
    suits: { spades: '#f2f5f7', hearts: '#ff6b9a', diamonds: '#ffc24a', clubs: '#389438' },
    joker: { colour: '#ff6b9a', tint: '#241726', edge: 'rgba(255,107,154,0.5)' },
    back: {
      background:
        'radial-gradient(circle at 30% 25%,#2b3556 0 2px,transparent 2px),'
        + 'radial-gradient(circle at 70% 60%,#2b3556 0 2px,transparent 2px),#0d1018',
      edge: '#2b3556',
      mark: '✦',
      markColour: '#4fd6e8',
    },
  },
  woodland: {
    id: 'woodland',
    name: 'Woodland',
    blurb: 'Warm and hand-drawn',
    face: '#f3e7cf',
    faceEdge: 'rgba(75,55,35,0.22)',
    // Warm black spades and a sea-green club, which olive and pine were not. Hearts are a
    // brick red so they stay apart from the club under red-green colour blindness.
    suits: { spades: '#16120d', hearts: '#9c190d', diamonds: '#c07a1e', clubs: '#147164' },
    joker: { colour: '#c07a1e', tint: '#efe0c2', edge: 'rgba(192,122,30,0.5)' },
    back: {
      background: 'repeating-linear-gradient(45deg,#6b4a2a 0 4px,#5a3d22 4px 8px)',
      edge: '#4a3119',
      mark: '❦',
      markColour: '#e3c9a1',
    },
  },
  clarity: {
    id: 'clarity',
    name: 'Clarity',
    blurb: 'Easiest to read',
    face: '#ffffff',
    faceEdge: '#111111',
    // The four-colour deck used in tournament play — the safest under a colour-blind filter.
    suits: { spades: '#111111', hearts: '#c1121f', diamonds: '#0057b8', clubs: '#087f5b' },
    joker: { colour: '#6a1b9a', tint: '#f3e8f8', edge: '#6a1b9a' },
    back: {
      background: 'repeating-linear-gradient(90deg,#111 0 6px,#fff 6px 12px)',
      edge: '#111111',
      mark: '',
      markColour: '#111111',
    },
  },
};

export const PACK_LIST = Object.values(PACKS);
export const DEFAULT_PACK: PackId = 'seaside';

const PACK_KEY = 'garbage.pack';

export function loadPack(): PackId {
  try {
    const saved = localStorage.getItem(PACK_KEY);
    return saved && saved in PACKS ? (saved as PackId) : DEFAULT_PACK;
  } catch {
    // Private browsing, or storage blocked. The default is perfectly good.
    return DEFAULT_PACK;
  }
}

export function savePack(pack: PackId): void {
  try {
    localStorage.setItem(PACK_KEY, pack);
  } catch {
    /* ignore */
  }
}
